import { DeviceService } from 'src/app/services/device.service';
import { ApiError } from 'src/app/models/api/api-error.model';
import { OnInit, inject, Component } from '@angular/core';
import { Device } from 'src/app/models/tech/device.model';

import { TechInsurance } from 'src/app/models/tech/tech-insurance.model';
import { TechBreaking } from 'src/app/models/tech/tech-breaking.model';
import { TechRelease } from 'src/app/models/tech/tech-release.model';
import { TechReturn } from 'src/app/models/tech/tech-return.model';
import { TechLoss } from 'src/app/models/tech/tech-loss.model';
import { TechService } from 'src/app/services/tech.service';
import { Tech } from 'src/app/models/tech/tech.model';
import { ActivatedRoute } from '@angular/router';

type TechHistoryBlock =
  | TechInsurance
  | TechLoss
  | TechBreaking
  | TechReturn
  | TechRelease
  | Tech;

type DeviceWithHistory = Device & {
  history: TechHistoryBlock[];
};

@Component({
  selector: 'app-device-details',
  templateUrl: './device-details.component.html',
  styleUrl: './device-details.component.scss',
})
export class DeviceDetailsComponent implements OnInit {
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly techService: TechService = inject(TechService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private deviceId?: string;

  public loading: boolean = true;
  public error?: ApiError = undefined;
  public device?: DeviceWithHistory;

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.deviceId = params['id'];
        this.getData();
      }
    });
  }

  private getData(): void {
    this.loading = true;
    this.error = undefined;
    this.deviceService
      .getInfo(this.deviceId)
      .subscribe({
        next: (data: any) => {
          console.info('DATA:', data);
          const device = new Device(data.data) as DeviceWithHistory;
          device.history = (data.data.history || []).map((block: any) =>
            this.mapTechBlock(block),
          );
          this.device = device;
        },
        error: (error: ApiError) => {
          this.error = error;
          console.error('Device Info Error:', error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  private mapTechBlock(block: any): TechHistoryBlock {
    const type = block['__type'];
    switch (type) {
      case 'insurance':
        return new TechInsurance(block);
      case 'loss':
        return new TechLoss(block);
      case 'breaking':
        return new TechBreaking(block);
      case 'return':
        return new TechReturn(block);
      case 'release':
        return new TechRelease(block);
      default:
        return new Tech(block);
    }
  }

  protected isInsurance(tech: TechHistoryBlock): tech is TechInsurance {
    return tech.__type === 'insurance';
  }

  protected isLoss(tech: TechHistoryBlock): tech is TechLoss {
    return tech.__type === 'loss';
  }

  protected isBreaking(tech: TechHistoryBlock): tech is TechBreaking {
    return tech.__type === 'breaking';
  }

  protected isReturn(tech: TechHistoryBlock): tech is TechReturn {
    return tech.__type === 'return';
  }

  protected isRelease(tech: TechHistoryBlock): tech is TechRelease {
    return tech.__type === 'release';
  }

  protected downloadDoc(docId?: string): void {
    if (!docId) return;
    this.techService.getDocument(docId).subscribe((res) => {
      let blob: Blob = res.body as Blob;
      let a = document.createElement('a');
      a.download =
        res.headers.get('originalname') ||
        `${new Date().toISOString()} - Tech Document (De Amicis Ring)`;
      a.href = window.URL.createObjectURL(blob);
      a.click();
    });
  }
}
